import { Fragment, useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, DollarSign, Loader2, TrendingDown, TrendingUp } from "lucide-react";
import { getPaymentHistory, getPayoutDetail } from "../payment-dues.api";
import type { Payout, PayoutItem, PayoutStatus } from "@shyne/shared/api";
import moment from "moment";

interface PaymentHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  recipientName: string;
  recipientId: string;
  totalAmount?: number;
  remainingAmount?: number;
}

type HistoryLabel = "paid" | "pending" | "failed" | "manual" | "extra";

const labelVariant: Record<HistoryLabel, "default" | "secondary" | "destructive" | "outline"> = {
  paid: "default",
  pending: "secondary",
  failed: "destructive",
  manual: "outline",
  extra: "secondary",
};

const historyLabel = (payout: Payout): HistoryLabel => {
  if (payout.status !== "paid") return payout.status;
  if (payout.extra) return "extra";
  return payout.method === "byHand" ? "manual" : "paid";
};

type LoadedItems = { state: "loading" } | { state: "failed"; message: string } | { state: "loaded"; items: PayoutItem[] };

const NOTHING_SHOWN = "—";

const NOT_APPLIED_LABEL: Record<PayoutStatus, string> = {
  pending: "Payment in progress",
  paid: "Also on another payment",
  failed: "Payment failed",
};

function PayoutItemRows({ loaded, status }: { loaded: LoadedItems; status: PayoutStatus }) {
  if (loaded.state === "loading") {
    return (
      <div className="flex items-center justify-center py-3">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (loaded.state === "failed") {
    return <div className="bg-destructive/10 text-destructive p-2 rounded-md text-sm">{loaded.message}</div>;
  }
  if (loaded.items.length === 0) {
    return <p className="text-muted-foreground text-sm py-2">This payment has no linked work.</p>;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Property</TableHead>
          <TableHead>Unit</TableHead>
          <TableHead>Date</TableHead>
          <TableHead className="text-right">Amount</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {loaded.items.map((item) => (
          <TableRow key={`${item.kind}-${item.id}`}>
            <TableCell className="text-sm">
              <div>{item.property?.name ?? NOTHING_SHOWN}</div>
              {item.kind === "workDay" && <div className="text-xs text-muted-foreground">Work day</div>}
              {!item.applied && (
                <Badge variant="outline" className="mt-1">
                  {NOT_APPLIED_LABEL[status]}
                </Badge>
              )}
            </TableCell>
            <TableCell className="text-sm">{item.unit ?? NOTHING_SHOWN}</TableCell>
            <TableCell className="text-sm">{item.date === null ? NOTHING_SHOWN : moment(item.date).format("MM/DD/YYYY")}</TableCell>
            <TableCell className="text-right text-sm">${item.amount.toFixed(2)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function PaymentHistoryModal({
  isOpen,
  onClose,
  recipientName,
  recipientId,
  totalAmount,
  remainingAmount,
}: PaymentHistoryModalProps) {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<Payout[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [openPayout, setOpenPayout] = useState<string | null>(null);
  const [itemsByPayout, setItemsByPayout] = useState<Record<string, LoadedItems>>({});

  useEffect(() => {
    if (!isOpen) {
      setRecords([]);
      setError(null);
      setOpenPayout(null);
      setItemsByPayout({});
      const timer = setTimeout(() => {
        document.body.style.pointerEvents = "";
      }, 350);
      return () => clearTimeout(timer);
    }

    const fetchHistory = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getPaymentHistory(recipientId);
        setRecords(data);
      } catch (err: any) {
        setError(err?.message ?? "Failed to load payment history.");
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [isOpen, recipientId]);

  const togglePayout = async (payoutId: string) => {
    if (openPayout === payoutId) {
      setOpenPayout(null);
      return;
    }
    setOpenPayout(payoutId);
    if (itemsByPayout[payoutId]?.state === "loaded") return;
    setItemsByPayout((held) => ({ ...held, [payoutId]: { state: "loading" } }));
    try {
      const { items } = await getPayoutDetail(payoutId);
      setItemsByPayout((held) => ({ ...held, [payoutId]: { state: "loaded", items } }));
    } catch (err: any) {
      setItemsByPayout((held) => ({ ...held, [payoutId]: { state: "failed", message: err?.message ?? "Failed to load what this payment paid." } }));
    }
  };

  const totalPaid = useMemo(
    () =>
      records.filter((r) => r.status === "paid").reduce((sum, r) => sum + r.amount, 0),
    [records]
  );

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Payment History - {recipientName}</SheetTitle>
          <SheetDescription>Past payments and payouts</SheetDescription>
        </SheetHeader>

        <div className="px-4 pb-4">
          {/* Stats Cards */}
          {!loading && (
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="rounded-lg border p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">Total Amount</p>
                </div>
                <p className="text-lg font-semibold">
                  ${(totalAmount ?? 0).toFixed(2)}
                </p>
              </div>
              <div className="rounded-lg border p-3 border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-900">
                <div className="flex items-center gap-1.5 mb-1">
                  <TrendingUp className="h-3.5 w-3.5 text-green-600" />
                  <p className="text-xs text-muted-foreground">Total Paid</p>
                </div>
                <p className="text-lg font-semibold text-green-600">
                  ${totalPaid.toFixed(2)}
                </p>
              </div>
              <div className="rounded-lg border p-3 border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-900">
                <div className="flex items-center gap-1.5 mb-1">
                  <TrendingDown className="h-3.5 w-3.5 text-destructive" />
                  <p className="text-xs text-muted-foreground">Amount Due</p>
                </div>
                <p className="text-lg font-semibold text-destructive">
                  ${(remainingAmount ?? 0).toFixed(2)}
                </p>
              </div>
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && (
            <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm">
              {error}
            </div>
          )}

          {!loading && !error && records.length === 0 && (
            <p className="text-muted-foreground text-sm text-center py-6">
              No payment history found.
            </p>
          )}

          {!loading && !error && records.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>
                    <span className="sr-only">What it paid</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((record) => {
                  const showing = openPayout === record.id;
                  const loaded = itemsByPayout[record.id];
                  return (
                    <Fragment key={record.id}>
                      <TableRow>
                        <TableCell className="text-sm">
                          {moment(record.createdAt).format("MM/DD/YYYY")}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          ${record.amount.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={labelVariant[historyLabel(record)]}>
                            {historyLabel(record)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {record.method === "stripe" ? "Stripe" : "Manual"}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-expanded={showing}
                            aria-label={`What the $${record.amount.toFixed(2)} payment paid`}
                            onClick={() => togglePayout(record.id)}
                          >
                            {showing ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </Button>
                        </TableCell>
                      </TableRow>
                      {showing && loaded !== undefined && (
                        <TableRow>
                          <TableCell colSpan={5} className="bg-muted/30">
                            <PayoutItemRows loaded={loaded} status={record.status} />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
